import type { ReactNode } from 'react';
import { useTechnical } from '@/app/technical';
import { Kicker } from './primitives';
import { useToast } from './Toast';

/**
 * What a record says to an agent, what earns a second reading, and a
 * document as a document (proposal 0009, SG-06).
 */

/**
 * "For agents": the ids and facts an agent resolves, one key per line, with
 * Copy. Never the page's prose — the side chat's context is identifiers
 * (§11), and a brief pasted into it is the same keys it would have sent.
 * A key whose value is absent is left out rather than written as empty.
 */
export function AgentBrief({ lines, label = 'For agents' }: { lines: Array<[string, string | number | boolean | null | undefined]>; label?: string }) {
  const toast = useToast();
  // The ids an agent resolves are the superadmin's to read; a person works
  // from the record itself, and asks the side chat about it by the page.
  const tech = useTechnical();
  const text = lines
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join('\n');
  if (!tech) return null;
  return (
    <section className="flex flex-col border border-line bg-raised" aria-label={label} data-testid="agent-brief">
      <div className="flex items-center justify-between gap-2.5 border-b border-line py-1 pl-3.5 pr-1">
        <Kicker>{label}</Kicker>
        <button
          type="button"
          className="min-h-tap min-w-tap px-3 text-micro uppercase tracking-button text-secondary hover:text-ink"
          onClick={() => {
            void navigator.clipboard?.writeText(text).then(
              () => toast({ text: 'Copied. Paste it into the chat and the agent resolves it by id.' }),
              () => toast({ text: 'The browser would not copy. Select the lines and copy them yourself.', tone: 'danger' }),
            );
          }}
        >
          Copy
        </button>
      </div>
      <pre className="m-0 whitespace-pre-wrap px-3.5 py-3 font-mono text-dense leading-relaxed text-ink [overflow-wrap:anywhere]">{text}</pre>
    </section>
  );
}

/**
 * The block on a record that is not merely additive — a price that rests
 * on an unconfirmed answer, a route with no legal basis yet. A rule in its
 * tone, what it is, why it matters, and the facts that make it so.
 */
export function DenseBlock({
  label,
  tone = 'danger',
  children,
  rows = [],
}: {
  label: string;
  tone?: 'danger' | 'warning';
  children: ReactNode;
  rows?: Array<[string, ReactNode]>;
}) {
  const edge = tone === 'danger' ? 'border-danger' : 'border-warning';
  const mark = tone === 'danger' ? 'bg-danger' : 'bg-warning';
  return (
    <section className={`flex flex-col border ${edge}`} aria-label={label} data-testid="dense-block">
      <div className="flex items-center gap-2.5 border-b border-line px-4 py-3.5">
        <span aria-hidden className={`block h-[9px] w-[9px] flex-none ${mark}`} />
        <span className="text-label font-semibold uppercase tracking-label">{label}</span>
      </div>
      <div className="flex flex-col gap-3.5 p-4">
        <div className="max-w-[64ch] text-body text-secondary">{children}</div>
        {rows.length > 0 && (
          <dl className="m-0 flex flex-col gap-px border border-line bg-line">
            {rows.map(([k, v]) => (
              <div key={k} className="grid grid-cols-[minmax(0,170px)_minmax(0,1fr)] gap-3.5 bg-page px-3.5 py-2.5 text-dense phone:grid-cols-1 phone:gap-0.5">
                <dt className="text-meta">{k}</dt>
                <dd className="m-0">{v}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}

type Block =
  | { kind: 'h1' | 'h2' | 'h3' | 'p' | 'meta' | 'bullet'; text: string }
  | { kind: 'table'; head: string[]; rows: string[][] }
  | { kind: 'gap' };

const inline = (x: string) => x.replace(/\*\*(.+?)\*\*/g, '$1').replace(/(^|\s)_(.+?)_(?=\s|$)/g, '$1$2');

/**
 * The document a Markdown body describes, line by line: headings, paragraphs,
 * bullets, an italic line as a note, and pipe tables. Exported so a test
 * reads what a report becomes; nothing in it renders HTML from the body.
 */
export function blocksOf(body: string): Block[] {
  const out: Block[] = [];
  let table: string[][] | null = null;
  const flush = () => {
    if (table && table.length > 0) out.push({ kind: 'table', head: table[0]!, rows: table.slice(1) });
    table = null;
  };
  for (const raw of body.split('\n')) {
    const line = raw.trim();
    if (line.startsWith('|')) {
      if (/^\|[\s|:-]+\|?$/.test(line)) continue; // the rule under a table's head
      (table ??= []).push(line.replace(/^\||\|$/g, '').split('|').map((c) => inline(c.trim())));
      continue;
    }
    flush();
    if (!line) out.push({ kind: 'gap' });
    else if (line.startsWith('### ')) out.push({ kind: 'h3', text: inline(line.slice(4)) });
    else if (line.startsWith('## ')) out.push({ kind: 'h2', text: inline(line.slice(3)) });
    else if (line.startsWith('# ')) out.push({ kind: 'h1', text: inline(line.slice(2)) });
    else if (line.startsWith('- ')) out.push({ kind: 'bullet', text: inline(line.slice(2)) });
    else if (/^_.*_$/.test(line)) out.push({ kind: 'meta', text: line.slice(1, -1) });
    else out.push({ kind: 'p', text: inline(line) });
  }
  flush();
  return out;
}

/**
 * A document as it will read in Drive: the report the platform composed
 * from the record, every figure with its source. Where it is filed, or that
 * it is not yet, is the caller's to say in `folder`; its actions (copy,
 * file to Drive, ask for a change) are the caller's too.
 */
export function DocView({ title, folder, body, actions }: { title: string; folder: string; body: string; actions?: ReactNode }) {
  const blocks = blocksOf(body);
  const lines = body.split('\n').length;
  return (
    <article className="flex flex-col border border-strong" aria-label={title} data-testid="doc-view">
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-raised px-4 py-3">
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-micro uppercase tracking-label text-meta">
            {folder} · {lines} lines
          </span>
          <span className="text-dense font-semibold">{title}</span>
        </span>
        {actions && <span className="flex flex-wrap gap-2">{actions}</span>}
      </header>
      <div className="flex max-w-[76ch] flex-col px-6 py-5 phone:px-4">
        {blocks.map((b, i) => {
          switch (b.kind) {
            case 'h1':
              return <h2 key={i} className="m-0 mb-2 text-screen font-semibold">{b.text}</h2>;
            case 'h2':
              return <h3 key={i} className="m-0 mb-1.5 mt-5 text-readback font-semibold">{b.text}</h3>;
            case 'h3':
              return <h4 key={i} className="m-0 mb-1 mt-3 text-body font-semibold">{b.text}</h4>;
            case 'meta':
              return <p key={i} className="m-0 text-dense text-meta">{b.text}</p>;
            case 'bullet':
              return (
                <p key={i} className="m-0 grid grid-cols-[14px_1fr] text-body">
                  <span aria-hidden>·</span>
                  <span>{b.text}</span>
                </p>
              );
            case 'p':
              return <p key={i} className="m-0 text-body">{b.text}</p>;
            case 'gap':
              return <span key={i} aria-hidden className="block h-2" />;
            case 'table':
              return (
                <div key={i} className="my-2 overflow-x-auto border border-line">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-raised">
                        {b.head.map((h) => (
                          <th key={h} className="border-b border-line px-2.5 py-2 text-left align-bottom text-micro font-medium uppercase tracking-[0.08em] text-meta">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {b.rows.map((r, ri) => (
                        <tr key={ri} className="border-b border-line align-top last:border-b-0">
                          {r.map((c, ci) => (
                            <td key={ci} className={`px-3 py-2.5 text-dense tabular-nums ${ci === 0 ? 'font-semibold' : ''}`}>
                              {c}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
          }
        })}
      </div>
    </article>
  );
}
